//@flow
import { useCallback, useEffect, useState } from 'react';
import getHasAgreedToTermsOfUse from '../../api/getHasAgreedToTermsOfUse';
import agreeToTermsOfUseOrRequestConsent from '../../api/agreeToTermsOfUseOrRequestConsent';
import useFormManager from '../../../../view/common/hooks/useFormManager';
import useDateOfBirthInput from '../../../../view/common/hooks/useDateOfBirthInput';
import useValidatedField from '../../../../view/common/hooks/useValidatedField';
import AgeHelper from '../../../../view/common/hooks/AgeHelper';

export default function useExistingUserTermsOfUse() {
    const [promptIsShowing, setPromptIsShowing] = useState(false);
    const [pageIndex, setPageIndex] = useState(0);
    const {
        showValidationErrors,
        setShowValidationErrors,
        notNullValidator
    } = useFormManager();

    const [isOver16, setIsOver16] = useState(null);
    const [isChecked, setIsChecked] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isNewUser, setIsNewUser] = useState(false);
    const [server500Error, setServer500Error] = useState(false);
    const canSubmitAge = (isOver16 && isChecked) || isOver16 === false;
    const toggleIsChecked = () => setIsChecked(!isChecked);
    const hasCheckboxError = showValidationErrors && !isChecked;
    const hasAgeConfirmationError = showValidationErrors && isOver16 === null;

    const {
        dateOfBirth,
        setDateOfBirth,
        dateOfBirthErrorMessage,
        dateOfBirthValidated
    } = useDateOfBirthInput();
    const isMinor = AgeHelper.isMinor(dateOfBirth);
    const hasDateOfBirthError =
        showValidationErrors && (!dateOfBirthValidated || !isMinor);

    const [
        parentEmail,
        setParentEmail,
        parentEmailValidated,
        setParentEmailValidated,
        parentEmailErrorMessage
    ] = useValidatedField(notNullValidator);
    const [serverErrorMessage, setServerErrorMessage] = useState('');
    const hasParentEmailError =
        showValidationErrors && (!parentEmailValidated || serverErrorMessage);
    const parentEmailHelperText = serverErrorMessage
        ? serverErrorMessage
        : parentEmailErrorMessage;

    const load = useCallback(async () => {
        const response = await getHasAgreedToTermsOfUse();
        if (response.ok === false) {
            return;
        }
        setIsNewUser(response.isNewUser);
        setPromptIsShowing(!response.hasAgreed);
    }, []);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(
        () => {
            if (isOver16 === false) {
                setIsChecked(false);
            }
        },
        [isOver16]
    );

    const selectUserOver16 = () => {
        setIsOver16(true);
    };

    const selectUserUnder16 = () => {
        setIsOver16(false);
    };

    const onClose = () => {
        setPageIndex(0);
        setIsOver16(null);
        setIsChecked(false);
        setIsSubmitting(false);
        setDateOfBirth('');
        setParentEmail('');
        setPromptIsShowing(false);
    };

    const onSubmit = async () => {
        setShowValidationErrors(true);
        setIsSubmitting(true);

        if (!canSubmitAge) {
            setIsSubmitting(false);
            return;
        }

        if (!isOver16) {
            setPageIndex(1);
            setShowValidationErrors(false);
            setIsSubmitting(false);
            return;
        }

        const response = await agreeToTermsOfUseOrRequestConsent(!isOver16);
        if (response.hasHttpError) {
            handleServerError();
            return;
        }

        setIsSubmitting(false);
        onClose();
    };

    const handleServerError = () => {
        setServer500Error(true);
        setIsSubmitting(false);
    };

    const onSubmitConsentRequest = async () => {
        setShowValidationErrors(true);
        setShowValidationErrors(true);
        if (!dateOfBirthValidated || !parentEmailValidated || !isMinor) {
            return;
        }

        setIsSubmitting(true);
        const response = await agreeToTermsOfUseOrRequestConsent(
            isMinor,
            parentEmail,
            dateOfBirth
        );

        if (Array.isArray(response)) {
            handleUserInputError(response);
        } else if (response.hasHttpError) {
            handleServerError();
        } else {
            handleSuccessfulConsentRequest();
        }

        setIsSubmitting(false);
    };

    const handleSuccessfulConsentRequest = () => {
        setShowValidationErrors(false);
        setPageIndex(2);
    };

    // eslint-disable-next-line flowtype/require-parameter-type
    const handleUserInputError = response => {
        setServerErrorMessage(response[0].parentGuardianEmail);
    };

    const handleGoBack = () => {
        setShowValidationErrors(false);
        setPageIndex(0);
    };

    return {
        promptIsShowing,
        isOver16,
        selectUserOver16,
        selectUserUnder16,
        isChecked,
        toggleIsChecked,
        canSubmitAge,
        hasCheckboxError,
        hasAgeConfirmationError,
        onSubmit,
        isSubmitting,
        isNewUser,
        server500Error,
        setServer500Error,
        pageIndex,
        dateOfBirth,
        setDateOfBirth,
        dateOfBirthErrorMessage,
        dateOfBirthValidated,
        hasDateOfBirthError,
        isMinor,
        parentEmail,
        setParentEmail,
        setParentEmailValidated,
        parentEmailErrorMessage,
        hasParentEmailError,
        parentEmailHelperText,
        onSubmitConsentRequest,
        handleGoBack,
        onClose
    };
}
